import { useMemo, useRef } from 'react';

import { useApiClient } from '@@common';

import { ConversationClient, DesignerChatSessionClient } from '../../api/api';
import { ChatMentionSuggestion, PromptChatAdapter } from './PromptChatAdapter';
import { createDesignerChatAdapter } from './createDesignerChatAdapter';

export const designerChatAgentIds = {
	architect: 'a8000000-0000-0000-0000-000000000001',
	userInterface: 'a2000000-0000-0000-0000-000000000001',
	flow: 'a3000000-0000-0000-0000-000000000001',
	template: 'a4000000-0000-0000-0000-000000000001',
	analyst: 'a5000000-0000-0000-0000-000000000001',
	domainModeler: 'a7000000-0000-0000-0000-000000000001',
} as const;

export const designerChatScopes = {
	architect: 'architect',
	contextCode: 'context-code',
	home: 'home',
	flow: 'flow',
	planning: 'planning',
	template: 'template',
	userInterface: 'user-interface',
	services: 'services',
} as const;

const searchNoMentions = async (): Promise<ChatMentionSuggestion[]> => [];

type UseDesignerChatAdapterOptions = {
	scopeName: string;
	agentId: string;
	targetId?: string;
	isAvailable?: boolean;
	missingTargetMessage: string;
	messageAttributes?: () => Record<string, string | undefined>;
	searchMentions?: (query: string) => Promise<ChatMentionSuggestion[]>;
};

function toMessageAttributes(attributes: Record<string, string | undefined> | undefined): Record<string, string> | undefined {
	if (!attributes) {
		return undefined;
	}

	const populatedAttributes: Record<string, string> = {};
	for (const [key, value] of Object.entries(attributes)) {
		if (value) {
			populatedAttributes[key] = value;
		}
	}

	return Object.keys(populatedAttributes).length > 0 ? populatedAttributes : undefined;
}

export function useDesignerChatAdapter({
	scopeName,
	agentId,
	targetId,
	isAvailable = !!targetId,
	missingTargetMessage,
	messageAttributes,
	searchMentions = searchNoMentions,
}: UseDesignerChatAdapterOptions): PromptChatAdapter {
	const sessionClient = useApiClient(DesignerChatSessionClient);
	const conversationClient = useApiClient(ConversationClient);
	const displayTargetId = targetId ?? `designer-chat-${scopeName}`;
	const pendingAttributes = useRef<{ requestId: string; attributes: Record<string, string> | undefined }>();

	return useMemo<PromptChatAdapter>(
		() =>
			createDesignerChatAdapter({
				getContextAttributes: () => {
					const targetKey = ({
						[designerChatScopes.flow]: 'FlowDefinitionId',
						[designerChatScopes.userInterface]: 'AppDefinitionId',
						[designerChatScopes.contextCode]: 'ContextId',
						[designerChatScopes.planning]: 'ProjectId',
					} as Record<string, string>)[scopeName] ?? 'TargetId';
					return { ...(targetId ? { [targetKey]: targetId } : {}), ...toMessageAttributes(messageAttributes?.()) };
				},
				agentId,
				cancelResponse: scopeName === designerChatScopes.contextCode ? undefined : (sessionId, turnId) => conversationClient.cancelResponse(sessionId, turnId),
				targetId: displayTargetId,
				sessionKey: `${scopeName}:${agentId}:${targetId ?? ''}`,
				isAvailable,
				retryableSends: scopeName !== designerChatScopes.contextCode,
				missingTargetMessage,
				sessionClient: {
					createOrGetSession: (_targetId, request) =>
						sessionClient.createOrGetSession(scopeName, agentId, {
							forceNew: request.forceNew,
							targetId,
						}),
					getSessions: (_targetId, pageIndex, pageSize) =>
						sessionClient.getSessions(scopeName, agentId, targetId, undefined, undefined, undefined, pageIndex, pageSize),
					updateSession: (_targetId, sessionId, request) =>
						sessionClient.updateSession(scopeName, agentId, sessionId, targetId, undefined, undefined, undefined, request),
					deleteSession: (_targetId, sessionId) => sessionClient.deleteSession(scopeName, agentId, sessionId, targetId, undefined, undefined, undefined),
					addParticipants: (_targetId, sessionId, userIds) =>
						sessionClient.addParticipants(scopeName, agentId, sessionId, targetId, undefined, undefined, undefined, { userIds }),
					getParticipants: async (_targetId, sessionId) => {
						const participants = await sessionClient.getParticipants(scopeName, agentId, sessionId, targetId, undefined, undefined, undefined);
						return participants.map((participant) => ({
							userId: participant.userId,
							name: participant.name,
							isOwner: participant.isOwner,
							canRemove: participant.canRemove,
						}));
					},
					removeParticipant: (_targetId, sessionId, userId) =>
						sessionClient.removeParticipant(scopeName, agentId, sessionId, userId, targetId, undefined, undefined, undefined),
					getMessagesDelta: (_targetId, sessionId, afterMessageId, afterSequence, changeToken, signal) =>
						sessionClient.getMessagesDelta(
							scopeName,
							agentId,
							sessionId,
							targetId,
							undefined,
							undefined,
							undefined,
							afterMessageId,
							afterSequence,
							changeToken,
							signal,
						),
					getMessagesBefore: (_targetId, sessionId, beforeSequence, pageSize) =>
						sessionClient.getMessagesBefore(scopeName, agentId, sessionId, targetId, undefined, undefined, undefined, beforeSequence, pageSize),
					uploadAttachment: async (file, conversationId, attributes) => {
						const attachment = await sessionClient.uploadAttachment(
							undefined,
							{ data: file, fileName: file.name },
							conversationId,
							attributes ? JSON.stringify(attributes) : undefined,
						);
						return attachment;
					},
					deleteDraftAttachment: (fileId) => sessionClient.deleteDraftAttachment(fileId),
					getAttachmentDownloadUrl: async (sessionId, fileId) => {
						const result = await sessionClient.getAttachmentDownload(sessionId, fileId);
						return result.url;
					},
					getAttachmentHtml: async (sessionId, fileId, signal) => {
						const result = await sessionClient.getAttachmentHtml(sessionId, fileId, signal);
						return result.html;
					},
				},
				async instruct(instruction, sessionId, conversationName, forceNew, _requestedAgentId, additionalMessageAttributes, fileIds, requestId) {
					const attributes = requestId && pendingAttributes.current?.requestId === requestId
						? pendingAttributes.current.attributes
						: toMessageAttributes({ ...messageAttributes?.(), ...additionalMessageAttributes });
					// Keep the exact screen snapshot for an uncertain send's same-request retry.
					if (requestId) pendingAttributes.current = { requestId, attributes };
					const response = await sessionClient.instruct(scopeName, agentId, {
						instruction,
						sessionId,
						conversationName,
						forceNew,
						targetId,
						messageAttributes: attributes,
						fileIds,
						requestId,
					});
					if (pendingAttributes.current?.requestId === requestId) pendingAttributes.current = undefined;
					return response;
				},
				searchMentions,
			}),
		[agentId, displayTargetId, isAvailable, messageAttributes, missingTargetMessage, scopeName, searchMentions, sessionClient, conversationClient, targetId],
	);
}
